import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useFocusEffect, useNavigation } from '@react-navigation/native';

import { ROLE_PERMISSIONS } from '../../../common/constants';
import { useUserContext } from '../../../common/utils/context';
import { isLegacyMobileUi } from '../../utils/legacyMobileUi';
import EmployeeMyDaySelectorDialog from '../myDay/EmployeeMyDaySelectorDialog';
import {
	buildEmployeeTrainingContext,
	canShowPickingWorkForMode,
	getEmployeeDayAccess,
} from '../myDay/employeeDayAccess';
import useMyDayMode, { MY_DAY_MODE } from '../myDay/useMyDayMode';
import { DRIVER_ROUTE, TAB_BAR_VARIANT } from './driverRoutes';
import useDriverDayModel from './useDriverDayModel';

// The shell has its own context so shell-only consumers (the tab bar) do not
// re-render with every driver day update.
const DriverShellContext = createContext(null);
const DriverDayContext = createContext(null);
const DriverDayStopSelectorContext = createContext(null);
const selectNoStop = () => false;

/**
 * App shell state shared by every screen of the main stack: the user's My day
 * mode, the bottom bar variant, the admin training session and ONE driver day
 * (useDriverDayModel) so the selected run, current stop and device-local skips
 * stay the same across Home, Start of day, On the run, the stop screen and End
 * of day. Mounted inside the drawer's main stack screen, around the stack.
 * Users on the legacy screens get the shell values but no driver day queries
 * (their classic board loads its own).
 */
const DriverDayProvider = ({ children }) => {
	const navigation = useNavigation();
	const { user, tenant, isAVC, hasPermission } = useUserContext();
	const isLegacyUi = isLegacyMobileUi(user);
	const { canAccessMyDayFeature, mode, refetchModeData, ticketScope } = useMyDayMode();
	const [trainingContext, setTrainingContext] = useState(null);
	const [isEmployeeSelectorOpen, setIsEmployeeSelectorOpen] = useState(false);
	const [focusedScreenCount, setFocusedScreenCount] = useState(0);

	const { canViewEmployeeDays, canEngageEmployeeRuns } = getEmployeeDayAccess({
		canAccessMyDayFeature,
		isAVC,
		user,
		hasPermission,
	});
	const canShowPickingWork = canShowPickingWorkForMode({ user, hasPermission, mode });
	const previewScopeKey = `${tenant?.id || ''}:${user?.id || ''}`;
	useEffect(() => {
		setTrainingContext(null);
		setIsEmployeeSelectorOpen(false);
	}, [canEngageEmployeeRuns, canViewEmployeeDays, previewScopeKey]);
	const activeTrainingContext = trainingContext &&
		trainingContext.scopeKey === previewScopeKey &&
		canEngageEmployeeRuns
		? trainingContext
		: null;

	// Drivers, cover drivers and tenants without My day (run list and stop
	// screen only) work a driver day; pickers and the no-run hub do not.
	const isDayEnabled = !isLegacyUi && (
		Boolean(activeTrainingContext) ||
		mode === MY_DAY_MODE.DRIVER ||
		mode === MY_DAY_MODE.CLASSIC
	);
	const day = useDriverDayModel({
		trainingContext: activeTrainingContext,
		isFocused: focusedScreenCount > 0,
		skip: !isDayEnabled,
	});
	const { refetchAll } = day;
	const refresh = useCallback(() => {
		const requests = isDayEnabled ? [refetchAll()] : [];
		if (!activeTrainingContext && refetchModeData) {
			requests.push(refetchModeData());
		}

		return Promise.all(requests);
	}, [activeTrainingContext, isDayEnabled, refetchAll, refetchModeData]);
	const dayValue = useMemo(() => ({ ...day, isEnabled: isDayEnabled, refresh }), [day, isDayEnabled, refresh]);

	const stopSelectorDayRef = useRef(null);
	useEffect(() => {
		stopSelectorDayRef.current = !isLegacyUi && isDayEnabled && !day.isTraining ? day : null;
	});
	const selectStop = useCallback((docketId) => {
		const currentDay = stopSelectorDayRef.current;
		const run = currentDay && docketId
			? currentDay.todaysRuns.find(item => (item.serviceDockets || []).some(docket => docket.id === docketId))
			: null;
		if (!run) {
			return false;
		}

		currentDay.setActiveRunId(run.id);
		currentDay.setSelectedDocketId(docketId);
		return true;
	}, []);
	// Same rule for a whole run (All Runs): one of the user's own runs today.
	const selectRun = useCallback((runId) => {
		const currentDay = stopSelectorDayRef.current;
		if (!currentDay || !runId || !currentDay.todaysRuns.some(run => run.id === runId)) {
			return false;
		}

		currentDay.setActiveRunId(runId);
		return true;
	}, []);

	const registerFocusedScreen = useCallback(() => {
		setFocusedScreenCount(count => count + 1);
		return () => setFocusedScreenCount(count => Math.max(0, count - 1));
	}, []);

	const startTraining = useCallback((employee, runId) => {
		const nextTrainingContext = canEngageEmployeeRuns && buildEmployeeTrainingContext({
			employee,
			runId,
			scopeKey: previewScopeKey,
		});
		if (nextTrainingContext) {
			// The employee (with its preview scope) lets "Back to preview" reopen the preview.
			setTrainingContext({ ...nextTrainingContext, employee });
		}

		return Boolean(nextTrainingContext);
	}, [canEngageEmployeeRuns, previewScopeKey]);
	const exitTraining = useCallback(() => setTrainingContext(null), []);
	const openEmployeeSelector = useCallback(() => setIsEmployeeSelectorOpen(true), []);
	const closeEmployeeSelector = useCallback(() => setIsEmployeeSelectorOpen(false), []);
	const selectEmployee = useCallback((employee) => {
		setIsEmployeeSelectorOpen(false);
		navigation.navigate('MainStack', {
			screen: DRIVER_ROUTE.EMPLOYEE_DAY_PREVIEW,
			params: { employee: { ...employee, previewScopeKey } },
		});
	}, [navigation, previewScopeKey]);

	const hasRunToday = day.todaysRuns.length > 0;
	const tabBarVariant = useMemo(() => {
		if (isLegacyUi) {
			return null;
		}
		// Admin training keeps the driver phases on the training run until it is turned off.
		if (activeTrainingContext) {
			return TAB_BAR_VARIANT.DRIVER;
		}
		if (mode === MY_DAY_MODE.PICKER) {
			return TAB_BAR_VARIANT.PICKER;
		}
		// While the first load runs (or failed), drivers keep their usual bar.
		if (mode === MY_DAY_MODE.DRIVER && (hasRunToday || day.loading || day.loadError)) {
			return TAB_BAR_VARIANT.DRIVER;
		}

		return TAB_BAR_VARIANT.BASIC;
	}, [activeTrainingContext, day.loadError, day.loading, hasRunToday, isLegacyUi, mode]);

	const shell = useMemo(() => ({
		isLegacyUi,
		isMyDayEnabled: canAccessMyDayFeature,
		mode,
		ticketScope,
		refetchModeData,
		tabBarVariant,
		canViewServiceRuns: hasPermission(ROLE_PERMISSIONS.VIEW_SERVICE_RUN_LIST_PAGE),
		canShowPickingWork,
		canViewEmployeeDays,
		canEngageEmployeeRuns,
		openEmployeeSelector,
		previewScopeKey,
		trainingContext: activeTrainingContext,
		startTraining,
		exitTraining,
		selectRun,
	}), [
		activeTrainingContext,
		canAccessMyDayFeature,
		canEngageEmployeeRuns,
		canShowPickingWork,
		canViewEmployeeDays,
		exitTraining,
		hasPermission,
		isLegacyUi,
		mode,
		openEmployeeSelector,
		previewScopeKey,
		refetchModeData,
		selectRun,
		startTraining,
		tabBarVariant,
		ticketScope,
	]);
	const value = useMemo(() => ({ day: dayValue, registerFocusedScreen }), [dayValue, registerFocusedScreen]);

	return (
		<DriverShellContext.Provider value={shell}>
			<DriverDayContext.Provider value={value}>
				<DriverDayStopSelectorContext.Provider value={selectStop}>
					{children}
				</DriverDayStopSelectorContext.Provider>
				{
					canViewEmployeeDays && !isLegacyUi && (
						<EmployeeMyDaySelectorDialog
							key={previewScopeKey}
							isOpen={isEmployeeSelectorOpen}
							canEngage={canEngageEmployeeRuns}
							onClose={closeEmployeeSelector}
							onSelect={selectEmployee}
						/>
					)
				}
			</DriverDayContext.Provider>
		</DriverShellContext.Provider>
	);
};

/**
 * Shell values (mode, tab bar variant, training, employee preview access) and
 * selectRun(runId): makes the run active and returns true when it is one of
 * the user's own runs today (same rule as useDriverDayStopSelector); stable.
 */
export const useDriverShell = () => useContext(DriverShellContext);

/**
 * The shared driver day for redesigned screens: the useDriverDayModel result
 * plus `isEnabled` and `refresh()`. While a screen using it is focused, the
 * day refetches on focus and polls pick top-up status like the classic board.
 */
export const useDriverDay = () => {
	const { day, registerFocusedScreen } = useContext(DriverDayContext);
	useFocusEffect(registerFocusedScreen);
	return day;
};

/**
 * For lists outside the driver flow that open a stop (service run detail):
 * returns selectStop(docketId), which makes that stop (and its run) the
 * current one when it belongs to the user's own driver day and returns true;
 * false otherwise (legacy UI, training, another driver's or day's run).
 * Call it before opening StopView. The function is stable, so list rows using
 * it do not re-render with every driver day update.
 */
export const useDriverDayStopSelector = () => useContext(DriverDayStopSelectorContext) || selectNoStop;

export default DriverDayProvider;
